/**
 * Norwegian Public Holidays (Red Days)
 *
 * Utility functions for checking if a date is a Norwegian public holiday.
 * Used for calendar and schedule displays.
 */

import { format, isSaturday, isSunday } from "date-fns";
import { nb } from "date-fns/locale";
import { formatAPIDate } from "./formatters";

/**
 * Norwegian public holidays with their Norwegian names.
 * Map of date string (YYYY-MM-DD) to Norwegian holiday name.
 */
const NORWEGIAN_HOLIDAYS: Record<string, string> = {
  // 2024
  "2024-01-01": "Nyttårsdag",
  "2024-03-28": "Skjærtorsdag",
  "2024-03-29": "Langfredag",
  "2024-04-01": "2. påskedag",
  "2024-05-01": "Arbeidernes dag",
  "2024-05-09": "Kristi himmelfartsdag",
  "2024-05-17": "Grunnlovsdag",
  "2024-05-20": "2. pinsedag",
  "2024-12-25": "1. juledag",
  "2024-12-26": "2. juledag",
  // 2025
  "2025-01-01": "Nyttårsdag",
  "2025-04-17": "Skjærtorsdag",
  "2025-04-18": "Langfredag",
  "2025-04-21": "2. påskedag",
  "2025-05-01": "Arbeidernes dag",
  "2025-05-17": "Grunnlovsdag",
  "2025-05-29": "Kristi himmelfartsdag",
  "2025-06-09": "2. pinsedag",
  "2025-12-25": "1. juledag",
  "2025-12-26": "2. juledag",
  // 2026
  "2026-01-01": "Nyttårsdag",
  "2026-04-02": "Skjærtorsdag",
  "2026-04-03": "Langfredag",
  "2026-04-06": "2. påskedag",
  "2026-05-01": "Arbeidernes dag",
  "2026-05-14": "Kristi himmelfartsdag",
  "2026-05-17": "Grunnlovsdag",
  "2026-05-25": "2. pinsedag",
  "2026-12-25": "1. juledag",
  "2026-12-26": "2. juledag",
};

/**
 * Check if a date is a Norwegian public holiday (red day).
 * Includes Sundays and official public holidays.
 * @param date Date to check
 * @returns true if the date is a red day
 */
export function isRedDay(date: Date): boolean {
  const dateStr = formatAPIDate(date);
  return isSunday(date) || dateStr in NORWEGIAN_HOLIDAYS;
}

/**
 * Check if a date is a weekend day (Saturday or Sunday) or a public holiday.
 * Used for weekend/holiday warning dialogs in booking forms.
 * Unlike isRedDay, this includes Saturdays which are not official red days
 * but are still non-working days for booking purposes.
 */
export function isWeekendOrHoliday(date: Date): boolean {
  const dateStr = formatAPIDate(date);
  return isSaturday(date) || isSunday(date) || dateStr in NORWEGIAN_HOLIDAYS;
}

/**
 * Check if a date is a Norwegian public holiday (excluding Sundays).
 * Use this when you need to distinguish between Sundays and official holidays.
 * @param date Date to check
 * @returns true if the date is a public holiday (not including regular Sundays)
 */
export function isPublicHoliday(date: Date): boolean {
  const dateStr = formatAPIDate(date);
  return dateStr in NORWEGIAN_HOLIDAYS;
}

/**
 * Get the Norwegian name of a public holiday.
 * Returns the holiday name if the date is a public holiday, undefined otherwise.
 * For Sundays that are not public holidays, returns "Søndag".
 * @param date Date to check
 * @returns Norwegian holiday name, "Søndag" for regular Sundays, or undefined
 */
export function getHolidayName(date: Date): string | undefined {
  const dateStr = formatAPIDate(date);
  const holidayName = NORWEGIAN_HOLIDAYS[dateStr];
  if (holidayName) {
    return holidayName;
  }
  if (isSunday(date)) {
    return "Søndag";
  }
  return undefined;
}

/**
 * Format tooltip content for day cells in schedule views.
 * Format: "Holiday name".
 * @param date Date to format
 * @returns Formatted tooltip string with holiday name
 */
export function formatDayCellTooltip(date: Date): string | null {
  const holidayName = getHolidayName(date);

  if (!holidayName) {
    return null;
  }

  return holidayName;
}

/**
 * Format tooltip content for date badges (without hours).
 * Format: "Holiday name\nMon 14.5" or just "Mon 14.5" for regular days.
 * @param date Date to format
 * @returns Formatted tooltip string with optional holiday name on first line
 */
export function formatDateTooltip(date: Date): string {
  const holidayName = getHolidayName(date);
  // Format: "Man" (capitalized short weekday without trailing period)
  const weekdayShort = format(date, "EEE", { locale: nb }).replace(".", "");
  const capitalizedWeekday =
    weekdayShort.charAt(0).toUpperCase() + weekdayShort.slice(1);
  // Format: "14.5" (day.month)
  const dateStr = format(date, "d.M");
  // Line: "Man 14.5"
  const dateLine = `${capitalizedWeekday} ${dateStr}`;

  if (holidayName) {
    return `${holidayName}\n${dateLine}`;
  }
  return dateLine;
}
